/**
 * @file        src/lib/discountRules.js
 * @description Shared value-validation helper for every admin form that writes a
 *              DISCOUNT — the auto discount rules (Flight/Hotel Discount List)
 *              and the promo codes (Flight/Hotel Coupon List, and their
 *              Corporate twins, which reuse the same pages).
 *
 *              One rule: a discount is worth something and never gives the whole
 *              fare away. The value must be GREATER THAN 0 for every discount
 *              type, and a PERCENTAGE discount may not exceed 100%. Above 100%
 *              the checkout silently clamps the discount back to the fare, so
 *              the money charged stops matching the rule the admin typed.
 *
 *              Mirrored server-side by admin_backend/src/utils/discountRules.js,
 *              which is the authoritative check — this layer exists so the admin
 *              sees the error on the field instead of as a save failure.
 * @author      Kinetika Labs
 * @created     2026-09-15
 */

export const MAX_PERCENT = 100;

// The discount tables spell the flat type 'flat', coupon_codes spells it
// 'fixed'. Both spell the proportional one 'percentage', so key off that.
export const isPercentType = (type) => String(type || '').toLowerCase() === 'percentage';

/**
 * Error message for a (type, value) pair, or null when it is acceptable.
 * A blank value returns the "greater than 0" message, so a required-but-empty
 * Discount Value reads as the real rule rather than a bare "Required".
 */
export function discountValueError(type, value) {
  if (value === '' || value == null) return 'Discount must be greater than 0.';
  const v = Number(value);
  if (!Number.isFinite(v)) return 'Discount must be a number.';
  if (v <= 0) return 'Discount must be greater than 0.';
  if (isPercentType(type) && v > MAX_PERCENT) return `A percentage discount cannot exceed ${MAX_PERCENT}%.`;
  return null;
}

/** The `max` for a number input — 100 for a percentage, undefined for a flat amount. */
export function discountValueMax(type) {
  return isPercentType(type) ? MAX_PERCENT : undefined;
}
